import React from 'react';
import { observer } from 'mobx-react';
import { SERVER_URL } from 'Constants';
import { appConfigStore } from 'Models/AppConfigStore';
import { store } from 'Models/Store';
import { Button } from 'Modules/Ui';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import alertToast from 'Util/ToastifyUtils';
import { exportEntityList } from '../EntityApi';

export interface CrudSeedingExportButtonProps {
	/** The seeding schema section this table maps to, e.g. `locations`. */
	section: string;
	dataTestId?: string;
}

/**
 * Downloads this table as a section of the client seeding file, ready to paste into a seeding document.
 *
 * <p>Super Administrators only, and hidden entirely for everyone else - a seed section is the shape used
 * to stand a whole deployment up, which is a different thing from the CSV the screens offer to staff. The
 * server enforces the same restriction; this only decides whether the button is worth showing.</p>
 */
function CrudSeedingExportButton({ section, dataTestId = 'crud-seeding-export-button' }: CrudSeedingExportButtonProps) {
	const [isExporting, setIsExporting] = React.useState(false);

	// Both gates: the deployment has to have the export switched on, and the viewer has to be a Super
	// Administrator. The endpoint enforces the same pair.
	if (!appConfigStore.seedingExportEnabled || !store.isSuper) {
		return null;
	}

	const handleExport = async () => {
		setIsExporting(true);
		try {
			await exportEntityList(`${SERVER_URL}/api/admin/seeding/${section}`, `${section}.json`);
		} catch (error) {
			const reason = error instanceof Error && error.message ? error.message : '';
			alertToast(reason || `Could not export ${section} as seeding data. Please try again.`, 'error');
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<Button
			variant="outline"
			size="sm"
			type="button"
			onClick={handleExport}
			disabled={isExporting}
			data-test-id={dataTestId}
		>
			<Icon name="code" useMaterialIcons />
			{isExporting ? 'Exporting...' : 'Export seed'}
		</Button>
	);
}

export default observer(CrudSeedingExportButton);
